import { useEffect, useRef, useState } from 'react';
import { UserFilterType, UserLocalStorage } from '@/entities/user';
import { useAuth } from '../auth.hooks';
import { RoleName } from '@/api/entities/user-role-type';
import debounce from 'lodash/debounce';
import { useLocalStorageState } from '../local-storage.hooks';

interface UserFilterProps {
  setCurrentPage: (page: number) => void;
}

export const useUserFilter = ({ setCurrentPage }: UserFilterProps) => {
  const { primaryRole, loggedInUser } = useAuth();
  const [debouncedFilter, setDebouncedFilter] = useState<UserFilterType>({
    key: '',
  });
  const filtersEmptyRef = useRef<boolean>(true);

  /**
   * === Filter section ===
   */
  const [key, setKey] = useLocalStorageState({
    key: UserLocalStorage.KEY,
    removeOnClose: true,
  });

  const [role, setRole] = useLocalStorageState({
    key: UserLocalStorage.ROLE,
    removeOnClose: true,
  });

  const [status, setStatus] = useLocalStorageState({
    key: UserLocalStorage.STATUS,
    removeOnClose: true,
  });

  const [groupId, setGroupId] = useLocalStorageState({
    key: UserLocalStorage.GROUP_ID,
    removeOnClose: true,
  });

  const [companyId, setCompanyId] = useLocalStorageState({
    key: UserLocalStorage.COMPANY_ID,
    removeOnClose: true,
  });

  const [branchId, setBranchId] = useLocalStorageState({
    key: UserLocalStorage.BRANCH_ID,
    removeOnClose: true,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      role: primaryRole !== RoleName.ADMIN ? primaryRole : undefined,
      userId: primaryRole !== RoleName.ADMIN ? loggedInUser?.id : undefined,
      key: key || '',
      roleFilter: role,
      groupId,
      companyId,
      branchId,
    }));

    filtersEmptyRef.current =
      !key && !role && !status && !groupId && !companyId && !branchId;
  }, [
    primaryRole,
    loggedInUser,
    key,
    role,
    status,
    groupId,
    companyId,
    branchId,
  ]);

  const debounceFilterUpdate = (value: string) => {
    setKey(value);
    debounce((value: string) => {
      setDebouncedFilter((prev) => ({ ...prev, key: value }));
    }, 1000);
  };

  const searchByRolesTypes = (value: string) => {
    setRole(value);
    setDebouncedFilter((prev) => ({ ...prev, roleFilter: value }));
  };

  const searchByStatus = (value: string) => {
    setStatus(value);
    setDebouncedFilter((prev) => ({ ...prev, status: value }));
  };

  const searchByGroup = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGroupId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, groupId: e.target.value }));
  };

  const searchByCompany = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanyId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, companyId: e.target.value }));
  };

  const searchByBranch = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBranchId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, branchId: e.target.value }));
  };

  const clearFilters = () => {
    setKey('');
    setRole('');
    setStatus('');
    setGroupId('');
    setCompanyId('');
    setBranchId('');
    setCurrentPage(1);
  };
  /**
   * === END OF Filter section ===
   */

  return {
    debouncedFilter,
    filtersEmpty: filtersEmptyRef.current,
    clearFilters,
    key,
    role,
    status,
    groupId,
    companyId,
    branchId,
    debounceFilterUpdate,
    searchByStatus,
    searchByRolesTypes,
    searchByGroup,
    searchByCompany,
    searchByBranch,
  };
};
